import type { ConnectionTarget } from './httpFormatter';

export function formatTargetHostHeader(target: ConnectionTarget): string {
  const hostname = target.hostname.trim();
  if (!hostname) {
    return '';
  }

  const defaultPort = target.https ? 443 : 80;
  return target.port === defaultPort ? hostname : `${hostname}:${target.port}`;
}

export function replaceHostHeader({
  requestText,
  host,
}: {
  requestText: string;
  host: string;
}): string {
  if (!host) {
    return requestText;
  }

  const lineEnding = requestText.includes('\r\n') ? '\r\n' : '\n';
  const lines = requestText.split(/\r?\n/);
  const firstHeaderIndex = 1;
  const headerEndIndex = lines.findIndex((line, index) => {
    return index > 0 && line.trim() === '';
  });
  const endIndex = headerEndIndex < 0 ? lines.length : headerEndIndex;
  const hostIndex = lines.findIndex((line, index) => {
    return (
      index >= firstHeaderIndex && index < endIndex && /^host\s*:/i.test(line)
    );
  });

  if (hostIndex >= 0) {
    lines[hostIndex] = `Host: ${host}`;
    return lines.join(lineEnding);
  }

  if (lines.length === 0 || !lines[0]) {
    return requestText;
  }

  lines.splice(firstHeaderIndex, 0, `Host: ${host}`);
  return lines.join(lineEnding);
}

export function getHostHeaderValue(requestText: string): string | null {
  const lines = requestText.split(/\r?\n/);
  const headerEndIndex = lines.findIndex((line, index) => {
    return index > 0 && line.trim() === '';
  });
  const endIndex = headerEndIndex < 0 ? lines.length : headerEndIndex;

  for (let index = 1; index < endIndex; index++) {
    const match = lines[index].match(/^host\s*:\s*(.*)$/i);
    if (match?.[1]?.trim()) {
      return match[1].trim();
    }
  }

  return null;
}

export function parseHostHeaderTarget(
  hostHeader: string,
): Partial<ConnectionTarget> | null {
  const host = hostHeader.trim();
  if (!host) {
    return null;
  }

  if (host.startsWith('[')) {
    const closingBracketIndex = host.indexOf(']');
    if (closingBracketIndex < 0) {
      return { hostname: host };
    }
    const hostname = host.slice(1, closingBracketIndex);
    const portText = host.slice(closingBracketIndex + 1).replace(/^:/, '');
    const port = Number(portText);
    return Number.isInteger(port) && port > 0
      ? { hostname, port }
      : { hostname };
  }

  const colonCount = (host.match(/:/g) ?? []).length;
  if (colonCount !== 1) {
    return { hostname: host };
  }

  const [hostname, portText] = host.split(':');
  const port = Number(portText);
  return Number.isInteger(port) && port > 0
    ? { hostname, port }
    : { hostname: host };
}

export function parseHostHeaderTargetForProtocol({
  hostHeader,
  https,
}: {
  hostHeader: string;
  https: boolean;
}): Partial<ConnectionTarget> | null {
  const target = parseHostHeaderTarget(hostHeader);
  if (!target) {
    return null;
  }

  return {
    ...target,
    port: target.port ?? (https ? 443 : 80),
  };
}
